import { Injectable, inject } from '@angular/core';

import { FsApi, RequestConfig } from '@firestitch/api';

import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { WindowMessageEventType } from '@app/common/enums';
import { Account } from '@app/common/interfaces';

import { WindowMessageService } from '../services/window-message.service';


@Injectable({
  providedIn: 'root',
})
export class AccountData {
  private _api = inject(FsApi);
  private _windowMessageService = inject(WindowMessageService);


  public create(data: Account = { id: null }) {
    data = { image: {}, address: {}, phone: null, ...data };
    data.fundUpdateNotification = data.fundUpdateNotification || false;
    data.fundAnomaliesEmails = data.fundAnomaliesEmails || false;

    return data;
  }

  public get(accountId, data: any = {}): Observable<any> {
    return this._api.get(`accounts/${accountId}`, data, { key: 'account' });
  }

  public gets(data = {}, config: RequestConfig = {}): Observable<any> {
    return this._api.request('GET', 'accounts', data, { key: 'accounts', ...config });
  }

  /** Reads the signed-in account's own account; no id is sent. */
  public getOwn(data: any = {}): Observable<any> {
    return this._api.get('account', data, { key: 'account' });
  }

  /** Reads a staff member from the Staff page, under Manage Staff on one of their organizations. */
  public getStaff(accountId, data: any = {}): Observable<any> {
    return this._api.get(`accounts/staff/${accountId}`, data, { key: 'account' });
  }

  /** Lists the staff of the organizations the signed-in account manages, for the Staff page. */
  public getsStaff(data = {}, config: RequestConfig = {}): Observable<any> {
    return this._api.request('GET', 'accounts/staff', data, { key: 'accounts', ...config });
  }

  public put(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}`, account, { key: 'account', ...config });
  }

  /** Saves the signed-in account's own profile; no id is sent. */
  public putOwn(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put('account', account, { key: 'account', ...config });
  }

  public delete(account): Observable<any> {
    return this._api.delete(`accounts/${account.id}`, account, { key: 'account' });
  }

  public image(account, file): Observable<any> {
    return this._api.post(`accounts/${account.id}/image`, { file }, { key: 'account' });
  }

  /** Uploads the signed-in account's own avatar; no id is sent. */
  public imageOwn(file): Observable<any> {
    return this._api.post('account/image', { file }, { key: 'account' });
  }

  public putPasswordChange(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}/password/change`, account, { key: 'account', ...config });
  }

  public putPasswordReset(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}/password/reset`, account, { key: 'account', ...config });
  }

  /** Saves a staff member from the Staff page, under Manage Staff; never a member holding an app-level role. */
  public putStaff(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/staff/${account.id}`, account, { key: 'account', ...config });
  }

  /** Resets a staff member's password from the Staff page, under Manage Staff; never a member holding an app-level role. */
  public putStaffPasswordReset(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/staff/${account.id}/password/reset`, account, { key: 'account', ...config });
  }

  public undelete(accountId, data: any = {}): Observable<any> {
    return this._api.post(`accounts/${accountId}/undelete`, data, { key: 'account' });
  }

  public notificationsUnread(data = {}): Observable<any> {
    return this._api.request('GET', 'accounts/notifications/unread', data, {
      handlers: false,
      headers: {
        ignoreLoadingBar: '',
      },
    })
      .pipe(
        map((response: any) => (response.data.unread)),
        tap((response) => this._windowMessageService
          .broadcast(WindowMessageEventType.NotificationsUnread, response),
        ),
      );
  }

}
